import { createApi } from '@reduxjs/toolkit/query/react';
import { createExtendedTokenBaseQuery } from './baseQuery';
import { RootState } from '../index';
import { WorkflowTemplate } from './workflowTemplateApi';

// Define interfaces
export interface LocationConditionPreset {
  id: string;
  name: string;
  description: string;
  condition_type: 'hierarchy_level' | 'geographic_region' | 'location_type';
  default_values: Record<string, unknown>;
  is_system_preset: boolean;
}

export interface LocationApprovalLevelPreset {
  id: string;
  name: string;
  description: string;
  approval_levels: Array<{
    level_number: number;
    description: string;
    approver_type: 'role' | 'user' | 'department';
    approver_id?: string;
    approver_role?: string;
  }>;
  is_system_preset: boolean;
}

export interface TemplateDocumentation {
  id: string;
  template_type: string;
  version: string;
  sections: Array<{
    title: string;
    content: string;
    order: number;
  }>;
  examples: Array<{
    title: string;
    description: string;
    code_snippet?: string;
  }>;
  last_updated: string;
}

export interface TemplateDiff {
  added: string[];
  removed: string[];
  modified: Array<{
    field: string;
    original: unknown;
    modified: unknown;
  }>;
}

// Create API
export const locationWorkflowTemplateApi = createApi({
  reducerPath: 'locationWorkflowTemplateApi',
  baseQuery: createExtendedTokenBaseQuery(''),
  tagTypes: ['LocationWorkflowTemplate'],
  endpoints: (builder) => ({
    getLocationConditionPresets: builder.query<LocationConditionPreset[], void>({
      query: () => 'workflow-templates/presets/location/',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'LocationWorkflowTemplate' as const, id })),
              { type: 'LocationWorkflowTemplate' as const, id: 'CONDITION_PRESETS' },
            ]
          : [{ type: 'LocationWorkflowTemplate' as const, id: 'CONDITION_PRESETS' }],
    }),
    
    getLocationApprovalPresets: builder.query<LocationApprovalLevelPreset[], void>({
      query: () => 'workflow-templates/approval-presets/location/',
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'LocationWorkflowTemplate' as const, id })),
              { type: 'LocationWorkflowTemplate' as const, id: 'APPROVAL_PRESETS' },
            ]
          : [{ type: 'LocationWorkflowTemplate' as const, id: 'APPROVAL_PRESETS' }],
    }),
    
    getLocationTemplateDocumentation: builder.query<TemplateDocumentation, void>({
      query: () => 'workflow-templates/documentation/location/',
      providesTags: [{ type: 'LocationWorkflowTemplate' as const, id: 'DOCUMENTATION' }],
    }),
    
    cloneSystemTemplate: builder.mutation<WorkflowTemplate, void>({
      query: () => ({
        url: 'workflow-templates/system/location/clone/',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'LocationWorkflowTemplate' as const, id: 'LIST' }],
    }),
    
    compareWithSystemTemplate: builder.query<TemplateDiff, string>({
      query: (templateId) => `workflow-templates/system/location/diff/?template_id=${templateId}`,
      providesTags: (result, error, templateId) => [
        { type: 'LocationWorkflowTemplate' as const, id: `DIFF-${templateId}` },
      ],
    }),
    
    applyConditionPreset: builder.mutation<
      { success: boolean; conditions: Record<string, unknown>[] },
      { template_id: string; preset_id: string; customizations?: Record<string, unknown> }
    >({
      query: (params) => ({
        url: 'workflow-templates/presets/location/apply/',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (result, error, { template_id }) => [
        { type: 'LocationWorkflowTemplate' as const, id: template_id },
      ],
    }),
    
    applyApprovalPreset: builder.mutation<
      { success: boolean; approval_levels: Record<string, unknown>[] },
      { template_id: string; preset_id: string; customizations?: Record<string, unknown> }
    >({
      query: (params) => ({
        url: 'workflow-templates/approval-presets/location/apply/',
        method: 'POST',
        body: params,
      }),
      invalidatesTags: (result, error, { template_id }) => [
        { type: 'LocationWorkflowTemplate' as const, id: template_id },
      ],
    }),
  }),
});

// Export hooks
export const {
  useGetLocationConditionPresetsQuery,
  useGetLocationApprovalPresetsQuery,
  useGetLocationTemplateDocumentationQuery,
  useCloneSystemTemplateMutation,
  useCompareWithSystemTemplateQuery,
  useApplyConditionPresetMutation,
  useApplyApprovalPresetMutation,
} = locationWorkflowTemplateApi;